// src/components/SendQuoteModal.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FiX,
  FiSend,
  FiAlertTriangle,
  FiLoader,
} from 'react-icons/fi';
import { prepareSend, sendQuote } from '../services/quoteService';

/* -------------------------------- utils -------------------------------- */

const stripUndefined = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null));

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function parseEmails(input) {
  if (!input) return [];
  return String(input)
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function useEscapeToClose(enabled, onClose) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onClose]);
}

function useFocusOnOpen(isOpen, ref) {
  useEffect(() => {
    if (isOpen && ref?.current) {
      const t = setTimeout(() => ref.current?.focus?.(), 0);
      return () => clearTimeout(t);
    }
  }, [isOpen, ref]);
}

/* ---------------------------- small UI helpers --------------------------- */

const Switch = ({ checked, onChange, disabled, id }) => (
  <label
    htmlFor={id}
    className={`relative inline-flex items-center ${
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
    }`}
  >
    <input
      type="checkbox"
      id={id}
      className="sr-only peer"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <div className="w-11 h-6 bg-neutral-200 dark:bg-neutral-700 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-400 dark:peer-focus:ring-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 dark:after:border-neutral-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-indigo-600" />
  </label>
);

const Field = ({ label, children, hint, error }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
      {label}
    </label>
    <div className="mt-2">{children}</div>
    {hint && !error && (
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>
    )}
    {error && (
      <p className="mt-1 text-xs text-rose-600 inline-flex items-center gap-1">
        <FiAlertTriangle /> {error}
      </p>
    )}
  </div>
);

/* -------------------------------- component ----------------------------- */

export default function SendQuoteModal({
  isOpen,
  onClose,
  quote,
  onSent,
  lastSentAt,
  loadPreview,
}) {
  const toInputRef = useRef(null);

  // robust id extraction
  const quoteId = useMemo(
    () =>
      quote?.id || quote?._id || quote?.quoteId
        ? String(quote.id || quote._id || quote.quoteId)
        : '',
    [quote]
  );

  const lifecycle = String(quote?.lifecycleStatus || quote?.status || '').toLowerCase();
  const requiresForce = lifecycle === 'accepted'; // backend requires override once accepted

  const clientEmail =
    quote?.client?.email || quote?.clientEmail || quote?.clientSnapshot?.email || '';
  const clientName =
    quote?.client?.name || quote?.clientName || quote?.clientSnapshot?.name || 'Client';
  const quoteNumber =
    quote?.quoteNumber || quote?.number || quote?.quoteNo || quoteId || '';

  // state
  const [toRaw, setToRaw] = useState(clientEmail);
  const [ccRaw, setCcRaw] = useState('');
  const [message, setMessage] = useState('');
  const [force, setForce] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);

  const [subjectPreview, setSubjectPreview] = useState('');
  const [pdfPeek, setPdfPeek] = useState({
    exists: false,
    filename: null,
    size: null,
    canAttach: true,
  });

  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  const [preview, setPreview] = useState(null);
  const [apiWarning, setApiWarning] = useState(null);

  const toList = useMemo(() => parseEmails(toRaw), [toRaw]);
  const ccList = useMemo(() => parseEmails(ccRaw), [ccRaw]);

  const invalidTo = useMemo(() => toList.filter((e) => !emailRegex.test(e)), [toList]);
  const invalidCc = useMemo(() => ccList.filter((e) => !emailRegex.test(e)), [ccList]);

  const anyBusy = preparing || submitting || previewBusy;

  useEscapeToClose(isOpen && !anyBusy, onClose);
  useFocusOnOpen(isOpen, toInputRef);

  // reset & prefill on open
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!isOpen || !quoteId) return;

      setToRaw(clientEmail || '');
      setCcRaw('');
      setMessage('');
      setForce(requiresForce); // ⬅️ force is required after acceptance
      setAttachPdf(false);
      setSubjectPreview('');
      setPdfPeek({ exists: false, filename: null, size: null, canAttach: true });
      setApiWarning(null);
      setPreview(null);

      setPreparing(true);
      try {
        const pre = await prepareSend(quoteId);
        if (cancelled) return;

        const toPrefill =
          Array.isArray(pre?.to)
            ? pre.to.join(', ')
            : typeof pre?.to === 'string'
            ? pre.to
            : Array.isArray(pre?.recipients)
            ? pre.recipients.join(', ')
            : clientEmail || '';

        const ccPrefill =
          Array.isArray(pre?.cc)
            ? pre.cc.join(', ')
            : typeof pre?.cc === 'string'
            ? pre.cc
            : '';

        setToRaw(toPrefill);
        setCcRaw(ccPrefill);
        // 👇 sync with backend: use `message` fallback to old `shortMessage`
        setMessage(pre?.message || pre?.shortMessage || '');
        setSubjectPreview(pre?.subject || '');
        setPdfPeek(pre?.pdf || { exists: false, filename: null, size: null, canAttach: true });
        // 👇 respect backend default for attachPdf if provided
        if (pre?.defaults && typeof pre.defaults.attachPdf === 'boolean') {
          setAttachPdf(pre.defaults.attachPdf);
        }
      } catch (e) {
        toast.warn(e?.message || 'Unable to prefill from server. You can still send.');
      } finally {
        setPreparing(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quoteId]);

  const relativeLastSent = useMemo(() => {
    if (!lastSentAt) return null;
    try {
      const then = new Date(lastSentAt).getTime();
      const now = Date.now();
      const diffSec = Math.max(0, Math.round((now - then) / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.round(diffMin / 60);
      if (diffH < 24) return `${diffH}h ago`;
      const diffD = Math.round(diffH / 24);
      return `${diffD}d ago`;
    } catch {
      return null;
    }
  }, [lastSentAt]);

  const inIdemWindow = useMemo(() => {
    if (!lastSentAt) return false;
    const then = new Date(lastSentAt).getTime();
    return Date.now() - then < 15 * 60 * 1000; // 15 minutes
  }, [lastSentAt]);

  const canSubmit =
    toList.length > 0 &&
    invalidTo.length === 0 &&
    invalidCc.length === 0 &&
    !submitting &&
    !preparing;

  const buildPayload = useCallback(() => {
    // Always include force=true for accepted quotes so backend override is explicit.
    const effectiveForce = requiresForce ? true : !!force;
    return stripUndefined({
      to: toList.length ? toList : undefined,
      cc: ccList.length ? ccList : undefined,
      message: message?.trim() ? message.trim() : undefined,
      attachPdf: attachPdf || undefined,
      force: effectiveForce ? true : undefined,
    });
  }, [toList, ccList, message, attachPdf, force, requiresForce]);

  const handlePreview = useCallback(async () => {
    if (!loadPreview) return;
    setPreviewBusy(true);
    setApiWarning(null);
    try {
      const payload = buildPayload();
      const data = await loadPreview(quoteId, payload);
      setPreview(data || null);
    } catch (e) {
      toast.error(e?.message || 'Unable to load preview.');
    } finally {
      setPreviewBusy(false);
    }
  }, [loadPreview, buildPayload, quoteId]);

  const handleSubmit = useCallback(async () => {
    if (!quoteId) {
      toast.error('Quote not found.');
      return;
    }
    if (!canSubmit) return;

    setSubmitting(true);
    setApiWarning(null);

    try {
      const payload = buildPayload();
      const result = await sendQuote(quoteId, payload);

      if (result.status === 'duplicate') {
        // Show precise guidance
        if (requiresForce) {
          toast.info('This quote is ACCEPTED. Re-sending requires Force Resend.');
        } else {
          toast.info('Already sent recently (idempotent). Enable “Force resend” to override.');
        }
        onSent?.(result);
        return; // keep modal open so user can flip Force
      }

      if (result.rateLimited) {
        toast.warn('Rate limit reached. Try again shortly.');
        setApiWarning({
          title: 'Rate limit reached',
          detail: 'Please wait a bit before trying again.',
        });
        onSent?.(result);
        return;
      }

      if (result.queued) {
        toast.success('Queued for delivery.');
      } else {
        toast.success('Quote sent.');
      }

      onSent?.(result);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const data = e?.response?.data || e?.data;

      if (status === 429) {
        setApiWarning({
          title: 'Rate limit reached',
          detail:
            data?.message || 'You have reached the sending limit. Please try again later.',
        });
        toast.warn('Rate limit reached.');
      } else if (status === 400 && data?.suppression) {
        setApiWarning({
          title: 'Address suppressed',
          detail:
            data?.suppression?.reason ||
            'The recipient is suppressed due to previous bounces or complaints.',
        });
        toast.warn('Recipient is suppressed.');
      } else if (status === 409) {
        if (requiresForce) {
          toast.info('This quote is ACCEPTED. Re-sending requires Force Resend.');
        } else {
          toast.info('Already sent recently (idempotent). Enable “Force resend” to override.');
        }
      } else {
        toast.error(e?.message || 'Failed to send quote.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [quoteId, canSubmit, buildPayload, onSent, onClose, requiresForce]);

  const onKeyDown = useCallback(
    (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [canSubmit, handleSubmit]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onKeyDown={onKeyDown}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300 ease-in-out"
        onClick={preparing || submitting || previewBusy ? undefined : onClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-quote-title"
        className="absolute inset-0 flex items-end sm:items-center justify-center p-4"
      >
        <div className="w-full sm:max-w-3xl rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl overflow-hidden dark:border dark:border-neutral-700/80">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50">
            <div className="min-w-0">
              <h3
                id="send-quote-title"
                className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate"
              >
                Send Quote
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
                {quoteNumber ? `Quote ${quoteNumber}` : 'This quote'} • {clientName}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={preparing || submitting || previewBusy}
              className="p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500 dark:text-neutral-400 disabled:opacity-50 transition-colors"
              aria-label="Close"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 space-y-5 relative max-h-[70vh] overflow-y-auto">
            {preparing && (
              <div className="absolute inset-0 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm z-10 pointer-events-none flex items-center justify-center">
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300">
                  <FiLoader className="animate-spin" />
                  Loading…
                </div>
              </div>
            )}

            {apiWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 dark:border-amber-700/50 px-4 py-3 text-sm">
                <div className="flex items-start gap-3">
                  <FiAlertTriangle className="mt-0.5 shrink-0 text-amber-500" />
                  <div>
                    <div className="font-medium dark:text-amber-100">
                      {apiWarning.title}
                    </div>
                    <div className="mt-0.5">{apiWarning.detail}</div>
                  </div>
                </div>
              </div>
            )}

            {/* subject */}
            {subjectPreview ? (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Subject
                </label>
                <p className="mt-2 text-sm text-neutral-800 dark:text-neutral-200 truncate">
                  {subjectPreview}
                </p>
              </div>
            ) : null}

            {/* fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="To"
                hint="Separate multiple emails with commas or spaces."
                error={
                  invalidTo.length
                    ? `Invalid email${invalidTo.length > 1 ? 's' : ''}: ${invalidTo.join(', ')}`
                    : undefined
                }
              >
                <input
                  ref={toInputRef}
                  type="text"
                  value={toRaw}
                  onChange={(e) => setToRaw(e.target.value)}
                  className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 transition"
                  placeholder="client@example.com"
                  disabled={preparing || submitting}
                  autoComplete="email"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {toList.slice(0, 6).map((e) => (
                    <span
                      key={e}
                      className="inline-flex items-center rounded-md bg-indigo-100/80 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2 py-1 text-xs font-medium"
                    >
                      {e}
                    </span>
                  ))}
                  {toList.length > 6 && (
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 py-1">
                      +{toList.length - 6} more
                    </span>
                  )}
                </div>
              </Field>

              <Field
                label="Cc (optional)"
                hint="Separate multiple emails with commas or spaces."
                error={
                  invalidCc.length
                    ? `Invalid email${invalidCc.length > 1 ? 's' : ''}: ${invalidCc.join(', ')}`
                    : undefined
                }
              >
                <input
                  type="text"
                  value={ccRaw}
                  onChange={(e) => setCcRaw(e.target.value)}
                  className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 transition"
                  placeholder="team@example.com"
                  disabled={preparing || submitting}
                  autoComplete="email"
                />
              </Field>
            </div>

            <Field
              label="Message (optional)"
              hint="Short note included at the top of the email."
            >
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 min-h-[96px] transition"
                placeholder="Hi! Please review and accept the quote. Let us know if you have any questions."
                disabled={preparing || submitting}
              />
            </Field>

            {/* toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 pt-1">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <label
                    htmlFor="attach-pdf-toggle"
                    className="font-medium text-neutral-800 dark:text-neutral-100 cursor-pointer"
                  >
                    Attach PDF
                  </label>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                    {pdfPeek?.exists && pdfPeek?.filename ? (
                      <span title={pdfPeek.filename}>{pdfPeek.filename}</span>
                    ) : (
                      'Attach a freshly rendered PDF.'
                    )}
                    {pdfPeek?.canAttach === false && (
                      <span className="ml-1.5 text-amber-600 dark:text-amber-500">
                        (Disabled)
                      </span>
                    )}
                  </div>
                </div>
                <Switch
                  id="attach-pdf-toggle"
                  checked={attachPdf}
                  onChange={(e) => setAttachPdf(e.target.checked)}
                  disabled={preparing || submitting || pdfPeek?.canAttach === false}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <label
                    htmlFor="force-resend-toggle"
                    className="font-medium text-neutral-800 dark:text-neutral-100 cursor-pointer"
                  >
                    Force Resend{' '}
                    {requiresForce && (
                      <span className="ml-1 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-700">
                        Required
                      </span>
                    )}
                  </label>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {requiresForce
                      ? 'Quote is accepted; override is required.'
                      : `Override 15min check${
                          relativeLastSent ? ` (${relativeLastSent})` : ''
                        }.`}
                    {!requiresForce && inIdemWindow && (
                      <span className="ml-1.5 text-amber-600">(within window)</span>
                    )}
                  </div>
                </div>
                <Switch
                  id="force-resend-toggle"
                  checked={requiresForce ? true : force}
                  onChange={(e) => (requiresForce ? null : setForce(e.target.checked))}
                  disabled={preparing || submitting || requiresForce}
                />
              </div>
            </div>

            {typeof loadPreview === 'function' && (
              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
                <div className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    Preview
                  </div>
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={previewBusy || preparing}
                    className="h-8 px-3 rounded-lg text-sm bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-600 disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
                  >
                    {previewBusy ? <FiLoader className="animate-spin" /> : null}
                    Refresh
                  </button>
                </div>
                <div className="p-4">
                  {preview ? (
                    <div className="space-y-3">
                      {preview.subject && (
                        <div>
                          <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                            Subject
                          </div>
                          <div className="text-sm text-neutral-900 dark:text-neutral-100">
                            {preview.subject}
                          </div>
                        </div>
                      )}
                      {preview.html ? (
                        <div>
                          <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                            HTML
                          </div>
                          <div
                            className="prose prose-sm max-w-none dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: preview.html }}
                          />
                        </div>
                      ) : preview.text ? (
                        <pre className="text-xs bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 whitespace-pre-wrap">
                          {preview.text}
                        </pre>
                      ) : (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                          No preview available.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-neutral-500 dark:text-neutral-400">
                      Click <span className="font-medium">Refresh</span> to fetch a preview.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={preparing || submitting}
                className="h-10 px-4 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="h-10 px-4 rounded-lg text-white shadow-sm flex items-center gap-2 disabled:opacity-50 bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
              >
                {submitting ? <FiLoader className="animate-spin" /> : <FiSend size={16} />}
                {attachPdf ? 'Send & Attach' : 'Send Quote'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
